import { z } from "zod";
import { TaskType } from "@remi/enums/task-type";

/**
 * Blocking task types
 */
export const ManualBlockingTaskTypeValues = [
  TaskType.SOW_DISCREPANCY,
  TaskType.SOW_DISCREPANCY_REVIEW,
  TaskType.SOW_MISSING_INFO,
  TaskType.SOW_ADDITIONAL_APPROVAL_NEEDED,
  TaskType.PENDING_HOA_APPROVAL,
  TaskType.HOMEOWNER_UNRESPONSIVE,
  TaskType.HOMEOWNER_UNWILLING_TO_MOVE_FORWARD,
  TaskType.HOMEOWNER_DECISION_DELAY,
  TaskType.MISQUOTE_CORRECTIONS,
  TaskType.HOMEOWNER_SOW_REJECTION,
  TaskType.PENDING_BUSINESS_LICENSING,
  TaskType.PRE_EXISTING_PERMITS,
  TaskType.UNPERMITTED_STRUCTURE,
  TaskType.ADDITIONAL_AHJ_REQUIREMENTS,
  TaskType.AHJ_AVAILABILITY_ISSUES,
  TaskType.MATERIALS_DELAY,
  TaskType.MISSING_FINDINGS,
  TaskType.PENDING_RELATED_PROJECT_PERMITTING,
  TaskType.PENDING_RELATED_PROJECT_SCHEDULING,
  TaskType.PENDING_RELATED_PROJECT_OTHER,
  TaskType.PENDING_INSPECTION_PROOF_OF_PASS,
  TaskType.PENDING_RELATED_PROJECT_COMPLETION_TO_INVOICE,
  TaskType.PENDING_RELATED_PROJECT_CORRECTIONS,
  TaskType.WAITING_ON_BUYER_TO_APPROVE_FSD_ACTION,
  TaskType.PENDING_EPC_TO_SCHEDULE_PV_FOR_BACKFILL,
  TaskType.PENDING_SALES_PROCESS,
  TaskType.CUSTOMER_NO_SHOW,
  TaskType.PENDING_CANCELLATION_REVIEW,
  TaskType.PENDING_FUNDING,
  TaskType.PENDING_LOAN_STIPS,
  TaskType.PENDING_SYSTEM_REDESIGN,
  TaskType.SOURCING_CONTRACTOR,
  TaskType.PENDING_REINSPECTION,
  TaskType.DAMAGED_PV_EQUIPMENT,
  TaskType.WORK_ORDER_UNCLEAR,
  TaskType.CONTRACTOR_REPORTED_SOW_ISSUE,
  TaskType.CONTRACTOR_SOW_QUESTION,
] as const satisfies TaskType[];

export const ManualBlockingTaskTypeSchema = z.enum(ManualBlockingTaskTypeValues);
export type ManualBlockingTaskType = (typeof ManualBlockingTaskTypeValues)[number];
export const ManualBlockingTaskType = ManualBlockingTaskTypeSchema.enum;

// Still a valid ManualBlockingTaskType so live rows keep their portal alerts and deadline impact, but
// no longer offered: the attempt reason of the same name declares a date hold that self-releases,
// whereas this blocking task never does (BOB-4166).
export const OfferedManualBlockingTaskTypeValues: ManualBlockingTaskType[] = ManualBlockingTaskTypeValues.filter(
  (taskType) => taskType !== TaskType.HOMEOWNER_DECISION_DELAY,
);

const SystemBlockingTaskTypeValues = [
  TaskType.SOW_CHANGE_REQUEST,
  TaskType.NON_REMI_ROOF_WORK_SCHEDULED,
  TaskType.NON_REMI_PV_INSTALL_SCHEDULED,
  TaskType.QUOTE_XA_ESTIMATE_MISMATCH,
  TaskType.XA_ESTIMATE_MISMATCH,
  TaskType.QUOTE_ON_SUPERSEDED_XA_ESTIMATE,
  TaskType.COC_AMOUNT_INCORRECT,
  TaskType.WORK_COMPLETION_DISPUTE,
  TaskType.COC_COPY_CHANGE_REQUEST,
  TaskType.STAMPED_PLANS_REQUIRED,
  TaskType.RETRIEVE_CAD,
  TaskType.REVIEW_WORK_ORDER_REJECTION,
  TaskType.APPROVE_WORK_ORDER_CONCESSION,
  TaskType.SUNRUN_FINDINGS_SUBMISSION_BLOCKED,
  TaskType.SUNRUN_FINDINGS_REJECTED,
  TaskType.SUNRUN_INVOICE_REJECTED,
  TaskType.SUNRUN_WORK_ORDER_REJECTED,
  TaskType.SUNRUN_WORK_ORDER_REJECTED_QA,
  TaskType.SUNRUN_FINDINGS_STALLED_IN_REVIEW,
  TaskType.SUNRUN_INVOICE_STALLED_IN_REVIEW,
  TaskType.SUNRUN_INVOICE_APPROVED_NOT_PAID,
  TaskType.SUNRUN_PAID_INVOICE_OUTSTANDING,
  TaskType.PARTNER_APPROVAL_PENDING,
  TaskType.CONTRACTOR_RECEIPT_MISSING,
  TaskType.WORK_ORDER_COST_DISPUTE,
  TaskType.INVOICE_NEEDS_ACCOUNTING_REVIEW,
  TaskType.MATERIAL_MATCH_PENDING,
  TaskType.REMI_ORDERED_MATERIALS_DELAY,
  TaskType.FUNDING_NOT_SECURED,
  TaskType.PARTNER_NEEDS_CORRECTED_DOCUMENTS,
  TaskType.PARTNER_DISPATCH_NOT_READY,
  TaskType.AWAITING_HOMEOWNER_COLOR_SELECTION,
  TaskType.PARTNER_DISPUTE_WITH_HOMEOWNER,
  TaskType.FINANCIAL_RESPONSIBILITY_UNDECIDED,
  TaskType.HOMEOWNER_REFUSES_TO_PAY,
  TaskType.BALANCE_OUTSTANDING_OR_MILESTONE_DISPUTED,
  TaskType.WESTHILL_FINDINGS_STALLED_IN_REVIEW,
  TaskType.WESTHILL_INVOICE_STALLED_IN_REVIEW,
  TaskType.PAYKEEPER_MILESTONE_NOT_CLAIMED,
  TaskType.PAYKEEPER_MILESTONE_STALLED_IN_REVIEW,
  TaskType.GET_PARTNER_SCOPE_INFORMATION,
  TaskType.GET_PARTNER_SCOPE_APPROVAL,
  TaskType.RESOLVE_PARTNER_SCOPE_MISMATCH,
  TaskType.GET_QUOTING_HELP,
  TaskType.SET_CUSTOM_QUOTE_PRICE,
  TaskType.SCHEDULE_REINSPECTION,
  TaskType.UNSIGNED_HIC_AFTER_NTP,
  TaskType.SOW_CHANGE_VERIFY,
  TaskType.REQUOTE_OR_ABANDON_SOW_CHANGE,
  TaskType.CREATE_HAND_SKETCH,
  TaskType.REVIEW_MEASUREMENT_REPORT_REQUEST,
] as const satisfies TaskType[];

export const BlockingTaskTypeValues = [
  ...ManualBlockingTaskTypeValues,
  ...SystemBlockingTaskTypeValues,
] as const satisfies TaskType[];

export const BlockingTaskTypeSchema = z.enum(BlockingTaskTypeValues);
export const BlockingTaskType = BlockingTaskTypeSchema.enum;
export type BlockingTaskType = (typeof BlockingTaskTypeValues)[number];

/**
 * Related project blocking task types
 */
const RelatedProjectBlockingTaskTypeValues = [
  BlockingTaskType.PENDING_RELATED_PROJECT_PERMITTING,
  BlockingTaskType.PENDING_RELATED_PROJECT_SCHEDULING,
  BlockingTaskType.PENDING_RELATED_PROJECT_COMPLETION_TO_INVOICE,
  BlockingTaskType.PENDING_RELATED_PROJECT_CORRECTIONS,
] as const satisfies BlockingTaskType[];

export const RelatedProjectBlockingTaskTypeSchema = z.enum(RelatedProjectBlockingTaskTypeValues);

/**
 * These are the blocking task types that will be displayed as alerts in the portal.
 */
export const HomeownerBlockingTaskTypeValues = [
  BlockingTaskType.PENDING_HOA_APPROVAL,
  BlockingTaskType.PRE_EXISTING_PERMITS,
  BlockingTaskType.UNPERMITTED_STRUCTURE,
  BlockingTaskType.HOMEOWNER_UNRESPONSIVE,
  BlockingTaskType.HOMEOWNER_UNWILLING_TO_MOVE_FORWARD,
  BlockingTaskType.HOMEOWNER_DECISION_DELAY,
  BlockingTaskType.HOMEOWNER_SOW_REJECTION,
] as const satisfies BlockingTaskType[];

export const HomeownerBlockingTaskTypeSchema = z.enum(HomeownerBlockingTaskTypeValues);
